// src/components/NotificationSign.tsx
import React from 'react';
import { useSocket } from "../../shared/socket/SocketProvider";
import { useNavigate, useLocation } from 'react-router-dom'; 
import { useTranslation } from 'react-i18next';

type NotificationSignProps = {
  to?: string;                // where to navigate on click (default: "/notifications")
  onOpen?: () => void;        // callback after click
  icon?: React.ReactNode;     // override icon (defaults to a clock)
  className?: string;
  style?: React.CSSProperties;
  ariaLabel?: string;         // button aria label
  persist?: boolean;          // keep the session counter across page reloads (default true)
  storageKey?: string;        // sessionStorage key when persist is true
};



const NotificationSign: React.FC<NotificationSignProps> = ({
  to = '/notifications',
  onOpen,
  icon,
  className,
  style,
  ariaLabel,
  persist = true,
  storageKey = 'notif.newCount',
}) => {
  const { socket, connected } = useSocket();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const finalAriaLabel = ariaLabel ?? t('notifications.open');

  const [count, setCount] = React.useState<number>(() => {
    if (!persist) return 0;
    const raw = sessionStorage.getItem(storageKey);
    const n = raw ? parseInt(raw, 10) : 0;
    return Number.isFinite(n) ? n : 0;
  });
  const RESET_EVENT = 'notifications:resetBadge';

  // keep sessionStorage in sync (if enabled)
  React.useEffect(() => {
    if (!persist) return;
    try { sessionStorage.setItem(storageKey, String(count)); } catch {}
  }, [count, persist, storageKey]);

  // Only count *new* notifications arriving via socket
  React.useEffect(() => {
  if (!socket || !connected) return;

  const onNew = () => setCount((c) => c + 1);
  socket.on("notification:new", onNew);

  return () => {
    socket.off("notification:new", onNew);
  };
}, [socket, connected]);


  //Reset when no border in notification
  React.useEffect(() => {
    const onReset = () => setCount(0);
    window.addEventListener(RESET_EVENT, onReset);
    return () => window.removeEventListener(RESET_EVENT, onReset);
  }, []);


  const handleClick = () => {
    setCount(0);          // clear the "new since looked" indicator
    onOpen?.();
    if (to) navigate(to); // open notifications page
  };

  const showBadge = count > 0;
  const display = count > 99 ? '99+' : String(count);

  //No Noti when closing Noti
  React.useEffect(() => {
    const target = to || '/notifications';
    // keep previous path to detect "leaving"
    const prevPathRef = (NotificationSign as any)._prevPathRef || { current: location.pathname };
    (NotificationSign as any)._prevPathRef = prevPathRef;
  
    const prev = prevPathRef.current;
    if (prev === target && location.pathname !== target) {
      setCount(0);
    }
    prevPathRef.current = location.pathname;
  }, [location.pathname, to]);

  return (
    <button
      type="button"
      onClick={handleClick}
      className={className}
      aria-label={finalAriaLabel}
      style={{
        position: 'relative',
        top: 4,   
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        padding: 0,
        ...style,
      }}
    >
      <span
  style={{
    fontSize: icon ? undefined : 20,
    lineHeight: 1,
    marginRight: 0,
    marginLeft: 0,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}
>
  {icon ?? '🔔'}
</span>

{showBadge && (
  <span
    aria-hidden
    style={{
      position: 'absolute',
      top: -6,
      right: -8,
      minWidth: 16,
      height: 16,
      padding: '0 4px',
      borderRadius: 999,
      background: '#115bee',
      color: '#fff',
      fontSize: 10,
      lineHeight: '16px',
      textAlign: 'center',
      boxShadow: '0 0 0 2px rgba(0,0,0,0.75)',
      userSelect: 'none',
      pointerEvents: 'none',
      boxSizing: 'border-box',
    }}
  >
    {display}
  </span> 
)}
    </button>
  );
};

export default NotificationSign;
